import React, { useEffect, useRef, useState } from "react";
import { ExternalLink, FileText, Loader2, RefreshCcw } from "lucide-react";

interface ReportCardPdfPreviewProps {
  /** Değişince PDF yeniden istenir (deneme + seçili sonuçlar + sonuç sürümü). */
  cacheKey: string;
  load: () => Promise<{ blob: Blob }>;
  /** Kaç öğrencinin karnesi hazırlanıyor (bekleme metni için). */
  count?: number;
}

/**
 * A4 önizleme: "Yazdır / İndir" ile basılan detaylı karne PDF'inin aynısı (tek şablon). Masaüstünde sayfa içinde
 * gösterilir; telefon tarayıcıları çerçevedeki PDF'i göstermediği (Android) ya da yalnız ilk sayfasını gösterdiği (iOS)
 * için orada yeni sekmede açılır.
 */
export default function ReportCardPdfPreview({ cacheKey, load, count = 1 }: ReportCardPdfPreviewProps) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const loadRef = useRef(load);
  loadRef.current = load;

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setUrl(null);
    setLoading(true);
    setError(null);
    // Seçim art arda değişirken her tıklamada PDF üretilmesin
    const timer = setTimeout(async () => {
      try {
        const { blob } = await loadRef.current();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || "Karne önizlemesi hazırlanamadı");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [cacheKey, attempt]);

  if (loading) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-3 rounded-xl border border-gray-200 bg-white px-4 py-16 text-center text-sm text-gray-500">
        <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
        {count > 1 ? `${count} öğrencinin detaylı karnesi hazırlanıyor…` : "Detaylı karne hazırlanıyor…"}
      </div>
    );
  }

  if (error || !url) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-3 rounded-xl border border-red-200 bg-white px-4 py-12 text-center text-sm text-red-600">
        <span>{error || "Karne önizlemesi hazırlanamadı"}</span>
        <button
          type="button"
          onClick={() => setAttempt((value) => value + 1)}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
          <RefreshCcw size={14} /> Tekrar dene
        </button>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500">
        <span>"Yazdır / İndir" ile basılacak detaylı karnenin aynısı</span>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden items-center gap-1 font-medium text-blue-600 hover:underline md:inline-flex">
          <ExternalLink size={14} /> Yeni sekmede aç
        </a>
      </div>
      <iframe
        title="Detaylı karne önizlemesi"
        src={url}
        className="hidden w-full rounded-lg border border-gray-300 bg-white md:block"
        style={{ height: "calc(100vh - 260px)", minHeight: 560 }}
      />
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center justify-center gap-2 rounded-xl bg-purple-600 px-4 py-3 text-sm font-medium text-white hover:bg-purple-700 md:hidden">
        <FileText size={16} /> Karneyi aç (PDF)
      </a>
    </div>
  );
}
